import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AnalyticsService } from '@common/services';
import { CommerceService } from '@common/services';
import { trackEvent } from '@intercom/messenger-js-sdk';
import { UserService } from '@modules/auth/services';
import protectedRoutes from '@modules/auth/services/protected-routes';
import { SBRouteData, SideNavItem } from '@modules/navigation/models';
import { NavigationService, SideNavService } from '@modules/navigation/services';
import objectHash from 'object-hash';
import { Subscription } from 'rxjs';

@Component({
    selector: 'sbpro-side-nav-item',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './side-nav-item-pro.component.html',
    styleUrls: ['side-nav-item-pro.component.scss'],
})
export class SideNavItemProComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('collapsibleSection') collapsibleSection!: ElementRef<HTMLDivElement>;

    @Input() sideNavItem!: SideNavItem;
    @Input() hierarchy: string[] = [];

    isActive = false;
    collapsed = true;
    routeData!: SBRouteData;
    navElement!: HTMLDivElement;
    id!: string;
    hierarchyExtension!: string[];
    subscription: Subscription = new Subscription();
    ready = false;

    constructor(
        private sideNavService: SideNavService,
        public navigationService: NavigationService,
        private router: Router,
        private changeDetectorRef: ChangeDetectorRef,
        private userService: UserService,
        private analyticsService: AnalyticsService,
        private commerceService: CommerceService,
    ) {}

    ngOnInit() {
        this.id = objectHash(this.sideNavItem);
        this.hierarchyExtension = [...this.hierarchy, this.id];

        // Handle URL changes to determine active state
        this.subscription.add(this.navigationService.currentURL$().subscribe(currentURL => this.determineIfActive(currentURL.replace(/#.*$/, ''), this.sideNavItem)));

        // Handle other menu items expanding/collapsing
        this.subscription.add(
            this.sideNavService.expand$.subscribe(ids => {
                if (!this.ready) return;

                const thisItemInExpandedHierarchy = ids.find(id => id === this.id);
                const sameLevelExpansion = ids.length === this.hierarchyExtension.length;

                // Close this menu if another one at the same level is expanded
                if (!thisItemInExpandedHierarchy && sameLevelExpansion && !this.collapsed) {
                    this.collapse();
                }
            }),
        );
        this.subscription.add(this.navigationService.currentURL$().subscribe(currentURL => this.determineIfActive(currentURL.replace(/#.*$/, ''), this.sideNavItem)));
    }

    ngAfterViewInit() {
        this.navElement = this.collapsibleSection.nativeElement;

        // Initial state setup
        setTimeout(() => {
            this.collapsed = !this.sideNavService.isExpanded(this.id) && !this.isActive;

            // Set initial height without animation
            if (!this.collapsed) {
                this.navElement.style.height = 'auto';
            } else {
                this.navElement.style.height = '0px';
            }

            this.ready = true;
            this.changeDetectorRef.markForCheck();
        });
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    determineIfActive(url: string, sideNavItem: SideNavItem) {
        // Check if this item or any of its children match the current URL
        this.isActive = this.isItemActive(url, sideNavItem);

        // If this item became active and we're ready, expand it
        if (this.isActive && this.ready && this.collapsed) {
            this.expand();
        }

        this.changeDetectorRef.markForCheck();
    }

    isItemActive(url: string, item: SideNavItem): boolean {
        // Check if the current item matches
        if (item.link && url.includes(item.link)) {
            return true;
        }

        // Check if any submenu items match
        if (item.submenu) {
            for (const submenu of item.submenu) {
                if (this.isItemActive(url, submenu)) {
                    return true;
                }
            }
        }

        return false;
    }

    toggle() {
        if (this.sideNavItem.link) {
            // Handle link navigation
            this.sideNavService.saveCache(this.hierarchyExtension, this.sideNavItem.link);
            this.analyticsService.analyticsCapture(`navigation_${this.sideNavItem.text}`);

            // Special cases
            if (this.sideNavItem.text === 'Nueva venta') {
                this.analyticsService.analyticsCapture('sale_opened sale', { lugar: 'sidenav-menu' });
            }
            if (this.sideNavItem.text === 'Utilidad/Perdida') {
                trackEvent('view_utility');
            }

            // External link handling
            if (this.sideNavItem.target === '_blank' && this.sideNavItem.link.startsWith('http')) {
                window.open(this.sideNavItem.link, '_blank');
                this.navigationService.closeSideNavIfOpenInMobile();
                return;
            }

            this.navigationService.closeSideNavIfOpenInMobile();
            return this.router.navigate([this.sideNavItem.link]);
        }

        // If component isn't ready yet, skip toggle
        if (!this.ready || !this.navElement) {
            return;
        }

        // Toggle expand/collapse
        this.collapsed ? this.expand() : this.collapse();
    }

    collapse() {
        if (!this.ready) return;

        this.sideNavService.setExpanded(this.id, false);
        this.navElement.style.height = `${this.navElement.scrollHeight}px`;

        // Force reflow
        this.navElement.offsetHeight;

        this.navElement.style.height = '0px';
        this.collapsed = true;
        this.changeDetectorRef.markForCheck();
    }

    expand() {
        if (!this.ready) return;

        this.sideNavService.setExpanded(this.id, true);
        this.navElement.style.height = `${this.navElement.scrollHeight}px`;
        this.collapsed = false;

        // Notify other items at the same level
        this.sideNavService.expand$.next(this.hierarchyExtension);

        // Remove fixed height after transition completes
        this.navElement.addEventListener(
            'transitionend',
            () => {
                if (!this.collapsed) {
                    this.navElement.style.height = 'auto';
                }
            },
            { once: true },
        );

        this.changeDetectorRef.markForCheck();
    }

    hideTab(submenu: any): boolean {
        // Check if this is the "Cierre de caja" menu item and cashbox is not enabled
        if (submenu.text === 'Caja') {
            const commerce = this.commerceService?._commerce$?.getValue();

            if (!commerce || !commerce.cashBox || !commerce.cashBox.enabled || !this.analyticsService.getExperimentVariant('cashbox')) {
                return true;
            }
            return false;
        }

        if (submenu.link) {
            const matchs = protectedRoutes.filter(pr => pr.route === submenu.link);

            if (matchs.length > 0) {
                const userPermissions: any = this.userService.user$.getValue().permissions;
                const match: boolean = userPermissions[`${matchs[0].module}`]?.includes(matchs[0]?.permission);
                return !match;
            }
        }
        return false;
    }
}
